import { type FC, useState } from "react";
import { Form } from "react-bootstrap";
import type {
	MetaAdsCreateTemplateDto,
	MetaAdsDatePreset,
	MetaAdsReportSection,
	MetaAdsReportTemplate,
} from "shared";
import { Modal, useModal } from "../../common/modal/Modal";
import { Select } from "../../components/Select";

const SECTION_OPTIONS: { value: MetaAdsReportSection; label: string }[] = [
	{ value: "kpis", label: "KPI Summary" },
	{ value: "trend_chart", label: "Trend Chart" },
	{ value: "campaigns", label: "Campaign Breakdown" },
	{ value: "ad_sets", label: "Ad Set Details" },
	{ value: "top_ads", label: "Top Ads" },
	{ value: "disapproved", label: "Disapproved Ads" },
];

const DATE_PRESETS: { value: MetaAdsDatePreset; label: string }[] = [
	{ value: "today", label: "Today" },
	{ value: "yesterday", label: "Yesterday" },
	{ value: "last_7_days", label: "Last 7 Days" },
	{ value: "last_30_days", label: "Last 30 Days" },
	{ value: "this_month", label: "This Month" },
	{ value: "last_month", label: "Last Month" },
];

interface ModalCreateTemplateProps {
	onSave: (dto: MetaAdsCreateTemplateDto) => Promise<void>;
	initial?: MetaAdsReportTemplate;
}

export const ModalCreateTemplate: FC<ModalCreateTemplateProps> = ({
	onSave,
	initial,
}) => {
	const { close } = useModal();
	const [name, setName] = useState(initial?.name ?? "");
	const [sections, setSections] = useState<MetaAdsReportSection[]>(
		initial?.sections ?? ["kpis", "trend_chart", "campaigns"],
	);
	const [datePreset, setDatePreset] = useState<MetaAdsDatePreset>(
		initial?.datePreset ?? "last_30_days",
	);
	const [comparisonEnabled, setComparisonEnabled] = useState(
		initial?.comparisonEnabled ?? false,
	);
	const [submitting, setSubmitting] = useState(false);

	const toggleSection = (section: MetaAdsReportSection) => {
		setSections((prev) =>
			prev.includes(section)
				? prev.filter((s) => s !== section)
				: [...prev, section],
		);
	};

	const handleSubmit = async () => {
		if (!name.trim() || sections.length === 0) return;
		setSubmitting(true);
		try {
			await onSave({ name, sections, datePreset, comparisonEnabled });
			close();
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Modal>
			<h5>{initial ? "Edit Template" : "Create Report Template"}</h5>

			<Form.Group className="mb-3">
				<Form.Label>Template Name</Form.Label>
				<Form.Control
					value={name}
					onChange={(e) => setName(e.target.value)}
					placeholder="e.g. Weekly Performance Report"
				/>
			</Form.Group>

			<Form.Group className="mb-3">
				<Form.Label>Sections</Form.Label>
				{SECTION_OPTIONS.map((opt) => (
					<Form.Check
						key={opt.value}
						type="checkbox"
						label={opt.label}
						checked={sections.includes(opt.value)}
						onChange={() => toggleSection(opt.value)}
					/>
				))}
			</Form.Group>

			<Form.Group className="mb-3">
				<Form.Label>Date Preset</Form.Label>
				<Select
					value={datePreset}
					onChange={(e) => setDatePreset(e.target.value as MetaAdsDatePreset)}
				>
					{DATE_PRESETS.map((p) => (
						<option key={p.value} value={p.value}>
							{p.label}
						</option>
					))}
				</Select>
			</Form.Group>

			<Form.Group className="mb-3">
				<Form.Check
					type="switch"
					label="Include previous period comparison"
					checked={comparisonEnabled}
					onChange={(e) => setComparisonEnabled(e.target.checked)}
				/>
				{comparisonEnabled && (
					<Form.Text className="text-muted">
						The report will include delta indicators comparing the selected
						period with the previous equivalent period.
					</Form.Text>
				)}
			</Form.Group>

			<div className="d-flex gap-2">
				<button
					type="button"
					className="btn btn-primary flex-grow-1"
					disabled={submitting || !name.trim() || sections.length === 0}
					onClick={handleSubmit}
				>
					{submitting ? "Saving..." : "Save"}
				</button>
				<button
					type="button"
					className="btn btn-outline-secondary"
					onClick={close}
				>
					Cancel
				</button>
			</div>
		</Modal>
	);
};
